/**
 * Comparison page content. One entry per competitor, one static route per entry
 * (prerenderer skips param routes, so each page is a thin wrapper component).
 *
 * Claims discipline: every factual claim about a competitor here is either
 * publicly documented by that competitor or sourced from
 * `projects/brands/fugue-lab/docs/strategy/business-model.md` competitive
 * research. Our own claims describe live capabilities only. Pricing for our
 * side lives on /packages (single source), so these pages link there instead
 * of hard-coding numbers.
 */

export interface CompareRow {
  dimension: string
  fugueLab: string
  them: string
}

export interface CompareFaq {
  question: string
  answer: string
}

export interface Competitor {
  slug: string
  /** Short name used in nav + index cards, e.g. "Acceptd" */
  name: string
  seoTitle: string
  seoDescription: string
  heroHeadline: string
  heroBody: string
  /** One honest paragraph: the short version of the whole page. */
  shortVersion: string
  rows: CompareRow[]
  /** Where the competitor genuinely fits. Honest, factual, no hedging. */
  whereTheyFit: { heading: string; body: string }
  switchSteps: { title: string; description: string }[]
  faqs: CompareFaq[]
  ctaHeading: string
  ctaBody: string
}

export const competitors: Competitor[] = [
  {
    slug: 'acceptd',
    name: 'Acceptd',
    seoTitle: 'Acceptd Alternative for Music Festivals',
    seoDescription:
      'Compare Fugue Lab and Acceptd for music festival applications. Flat monthly pricing with no per-applicant fees, music-native application fields, and your website and applications on one system.',
    heroHeadline: 'The Acceptd alternative for festivals that want their website and applications on one system',
    heroBody:
      'Acceptd handles application intake. Your website, your season content, and your applicant communications still live somewhere else. Fugue Lab runs all of it together, for a flat monthly rate with no per-applicant fees.',
    // Acceptd's ORG-side pricing is unverified and must not be asserted here.
    // What is sourced: pricing is gated behind a sales call with no public
    // tiers, applicants pay $60 to $85 per program (observed at six named
    // festivals), and Togetherwork acquired them in 2021. See the competitive
    // table in projects/brands/fugue-lab/CLAUDE.md and @gjl/voice rule 20.
    shortVersion:
      'Acceptd is the incumbent for conservatory and festival admissions. Its pricing is gated behind a sales call, and its applicants pay $60 to $85 per program to apply. It was acquired by Togetherwork, a private equity rollup, in 2021. Fugue Lab was built by someone who has been on all three sides of an audition: applying to programs, sitting on the panel, and building the systems that run the cycle. You get music-native application forms, review workflow, and the festival website itself, maintained for you, at a flat monthly rate. Nobody pays to apply.',
    rows: [
      {
        dimension: 'Pricing model',
        fugueLab: 'Flat monthly rate. No per-applicant fees. See packages for tiers.',
        them: 'Pricing gated behind a sales call, with no public tiers. Applicants pay $60 to $85 per program.',
      },
      {
        dimension: 'Your website',
        fugueLab: 'Included. Applications live on your own site, in your own design.',
        them: 'Not included. Applicants leave your site to apply on getacceptd.com.',
      },
      {
        dimension: 'Music-native fields',
        fugueLab:
          'Repertoire lists, per-movement audition video, teacher recommendations, instrument and voice-part routing.',
        them: 'Media upload and forms built for arts admissions generally.',
      },
      {
        dimension: 'Who builds and runs it',
        fugueLab: 'A working organist and software engineer, directly. You talk to the person who builds it.',
        them: 'Support queue at a private-equity-owned platform (Togetherwork, 2021).',
      },
      {
        dimension: 'Applicant communications',
        fugueLab: 'Email sequences for applicants and admits, on the same system as the forms.',
        them: 'In-platform messaging; your season marketing still lives elsewhere.',
      },
      {
        dimension: 'Contract shape',
        fugueLab: 'Month to month. Your content and applicant data export cleanly if you leave.',
        them: 'Annual agreements are standard.',
      },
    ],
    whereTheyFit: {
      heading: 'Where Acceptd fits',
      body: 'Acceptd runs admissions for many of the largest conservatories and S-tier festivals, and its applicant marketplace puts your program in front of students already browsing there. If marketplace discovery is the main thing you want, that network is real. If what you want is your own applications, on your own site, without paying per applicant, that is what we build.',
    },
    switchSteps: [
      {
        title: 'We map your current application',
        description:
          'Every field, fee, deadline, and reviewer step from your existing Acceptd setup, documented before anything moves.',
      },
      {
        title: 'We rebuild it on your site',
        description: 'Music-native forms in your own design, tested with your team before applications open.',
      },
      {
        title: 'You open the season',
        description:
          'Applicants apply on your domain. Your team reviews in one place. We stay on for support through the cycle.',
      },
    ],
    faqs: [
      {
        question: 'Can you handle audition video uploads?',
        answer:
          'Yes. Applicants upload video per piece or per movement, and reviewers watch inside the review workflow. Reliable large-file upload was a day-one requirement, not an add-on.',
      },
      {
        question: 'What happens to our historical applicant data?',
        answer:
          'Export it from Acceptd and we import it, so prior-year applicants and their materials stay searchable alongside new seasons.',
      },
      {
        question: 'Our applications open in a few months. Is that enough time?',
        answer:
          'A typical application rebuild takes weeks, not months. Tell us your open date on the call and you get a straight answer about whether it is realistic.',
      },
    ],
    ctaHeading: 'Run applications on your own site',
    ctaBody:
      'Book a call and walk through your current Acceptd setup. You leave with a concrete picture of what the same season looks like on Fugue Lab, and what it costs.',
  },
  {
    slug: 'submittable',
    name: 'Submittable',
    seoTitle: 'Submittable Alternative for Music Auditions',
    seoDescription:
      'Compare Fugue Lab and Submittable for music festival applications and auditions. Music-native repertoire and per-movement video fields, flat monthly pricing, and applications on your own website.',
    heroHeadline: 'The Submittable alternative for programs whose applications are music',
    heroBody:
      'Submittable manages submissions of every kind: grants, manuscripts, scholarships, auditions. That breadth is why it works for so many organizations, and why a music application ends up described in fields that were designed for something else.',
    shortVersion:
      'Submittable is a general submission-management platform with an auditions use case, and organizations that run several different intake processes get real value from having them in one tool. Fugue Lab does one thing: applications and auditions for music programs, on the program’s own website. Repertoire is a structured field with composer and movement, audition video uploads per excerpt, and recommendations are requested and chased separately from the application itself. If music intake is the only intake you run, the specific tool beats the general one.',
    rows: [
      {
        dimension: 'What it is built for',
        fugueLab: 'Music applications and auditions specifically. Nothing else shares the field model.',
        them: 'Submissions generally: grants, publishing, scholarships, awards, auditions.',
      },
      {
        dimension: 'Repertoire',
        fugueLab: 'Structured fields for composer, work, and movement, so reviewers can sort and compare.',
        them: 'Free-text or custom fields you configure yourself.',
      },
      {
        dimension: 'Audition video',
        fugueLab: 'Upload per piece or per movement, watched inside the review workflow.',
        them: 'File and media attachments on the submission.',
      },
      {
        dimension: 'Your website',
        fugueLab: 'Included. The application is a page on your own site, in your own design.',
        them: 'Not included. The form is hosted and branded as a Submittable page.',
      },
      {
        dimension: 'Pricing',
        fugueLab: 'Flat monthly rate, published on our pricing page. Month to month.',
        them: 'Quoted per organization through sales, typically on an annual plan.',
      },
      {
        dimension: 'Who you talk to',
        fugueLab: 'A working organist and software engineer, directly.',
        them: 'Account management and a support queue.',
      },
    ],
    whereTheyFit: {
      heading: 'Where Submittable fits',
      body: 'If your organization runs grant cycles, a commission competition, scholarships, and auditions, Submittable puts all of it in one place with one team trained on one tool, and that consolidation is worth a great deal. Its reporting and reviewer management are mature. We are the better fit when auditions and applications are the whole job and you want the form to speak music rather than be configured into it.',
    },
    switchSteps: [
      {
        title: 'We map your current form',
        description:
          'Every field, attachment, fee, and reviewer stage in your existing Submittable setup, documented before anything moves.',
      },
      {
        title: 'We rebuild it music-native',
        description:
          'Free-text repertoire becomes structured fields. Media attachments become per-movement video. Tested with your faculty before you open.',
      },
      {
        title: 'You open the season',
        description:
          'Applicants apply on your domain, reviewers score in one place, and we stay on for support through the cycle.',
      },
    ],
    faqs: [
      {
        question: 'We use Submittable for grants too. Do we have to move everything?',
        answer:
          'No, and usually you should not. Keep Submittable for grant intake and run auditions here. They are different processes with different reviewers, and nothing about our system requires you to consolidate.',
      },
      {
        question: 'Can we keep our historical submissions?',
        answer:
          'Export them from Submittable and we import them, so prior applicants and their materials stay searchable alongside new seasons.',
      },
      {
        question: 'How long does a rebuild take?',
        answer:
          'Weeks rather than months for a typical application. Bring your open date to the call and you get a straight answer about whether it is realistic.',
      },
    ],
    ctaHeading: 'Put your auditions in a tool that knows what a movement is',
    ctaBody:
      'Book a call and walk through your current Submittable form. You leave with a concrete picture of the same cycle running on your own site, and what it costs.',
  },
  {
    slug: 'yaptracker',
    name: 'YAP Tracker',
    seoTitle: 'YAP Tracker Alternative for Music Programs',
    seoDescription:
      'Compare Fugue Lab and YAP Tracker for audition and application management. YAP Tracker is a listing marketplace singers subscribe to; Fugue Lab runs applications and auditions on your own website.',
    heroHeadline: 'YAP Tracker lists your program. It does not run your applications.',
    heroBody:
      'These are different jobs, and it is worth being clear about which one you are trying to solve. YAP Tracker puts your audition in front of singers who are already looking. Fugue Lab is the system those applicants apply through once they arrive.',
    shortVersion:
      'YAP Tracker is a discovery marketplace for the opera world: companies post openings, and singers pay a subscription to track deadlines and manage their own applications. It is very good at what it does, and for a young artist program recruiting singers it is often worth keeping. What it is not is your application system. Fugue Lab is where the application actually lives: your fields, your fees, your faculty review, on your own domain. Most programs that use both keep the listing and move the intake.',
    rows: [
      {
        dimension: 'What it is',
        fugueLab: 'An application and audition system you run your cycle in.',
        them: 'A listing and deadline-tracking marketplace applicants subscribe to.',
      },
      {
        dimension: 'Who pays',
        fugueLab: 'Your organization pays a flat monthly rate. Applicants pay only your application fee.',
        them: 'Singers pay a subscription for access. Posting an opening is free to companies.',
      },
      {
        dimension: 'Repertoire and audition video',
        fugueLab: 'Structured repertoire, per-movement video upload, reviewed inside the workflow.',
        them: 'Applicants assemble and send materials themselves.',
      },
      {
        dimension: 'Where applicants apply',
        fugueLab: 'On your own website, in your own design.',
        them: 'Varies by listing. Often an email address or an external form.',
      },
      {
        dimension: 'Faculty review',
        fugueLab: 'Scoring, notes, and accept/waitlist/decline decisions with the emails attached.',
        them: 'Not its purpose. Review happens wherever you already do it.',
      },
      {
        dimension: 'Repertoire beyond opera',
        fugueLab: 'Instrumental, choral, conducting, and composition programs all use the same field model.',
        them: 'Focused on the opera and young artist world.',
      },
    ],
    whereTheyFit: {
      heading: 'Where YAP Tracker fits',
      body: 'If you are a young artist program recruiting singers, YAP Tracker reaches an audience that is already actively looking for exactly what you are offering, and no application system replaces that reach. Keep the listing. The question is only where the applicant goes after they click, and whether that destination is your site or an inbox.',
    },
    switchSteps: [
      {
        title: 'Keep the listing',
        description:
          'Nothing here asks you to leave YAP Tracker. It stays the front door for singers already browsing.',
      },
      {
        title: 'We build the application behind it',
        description:
          'Your fields, fees, and repertoire structure on your own domain, so the listing points at a real application instead of an email address.',
      },
      {
        title: 'Review in one place',
        description:
          'Faculty score inside the system, decisions send the emails attached to them, and the applicant record persists into next season.',
      },
    ],
    faqs: [
      {
        question: 'Should we stop posting on YAP Tracker?',
        answer:
          'Almost certainly not. Discovery and intake are separate problems and the listing is doing the first one. Point it at your application page instead of an inbox and both get better.',
      },
      {
        question: 'We only run one audition a year. Is a system worth it?',
        answer:
          'Sometimes not, and we will tell you so on the call. The break-even is usually the point where chasing recommendations and re-encoding video costs more staff time than the subscription.',
      },
      {
        question: 'We are not an opera program. Does this still apply?',
        answer:
          'Yes. Instrumental festivals, choral programs, conducting workshops, and composition residencies run the same intake shape, and the field model covers all of them.',
      },
    ],
    ctaHeading: 'Give your listing somewhere real to point',
    ctaBody:
      'Book a call and bring the listing you already run. You leave with a concrete picture of what applicants see after they click, and what it costs.',
  },
  {
    slug: 'squarespace',
    name: 'Squarespace',
    seoTitle: 'Squarespace Alternative for Music Organizations',
    seoDescription:
      'Compare Fugue Lab and Squarespace for music festivals, ensembles, schools, and churches. Original design, music-native structure, and a site someone runs for you instead of a template you maintain yourself.',
    heroHeadline: 'What Squarespace templates cannot do for a music organization',
    heroBody:
      'Squarespace is fine for a portfolio. A festival with applications, a season calendar, faculty bios, and four programs is not a portfolio. At some point you stop designing your site and start fighting the template.',
    shortVersion:
      'Squarespace gives you a template and an editor, and everything past that is your job: the design decisions, the updates, the workarounds when the template will not do what your season needs. Fugue Lab designs your site around how music organizations actually work, with programs, rosters, events, and application funnels as first-class structure, then keeps running it with you. You edit content through a website editor that cannot break the design.',
    rows: [
      {
        dimension: 'Design',
        fugueLab: 'Designed from scratch around your organization and season. No template underneath.',
        them: 'Template you customize within its limits. Sites in the same template family look related.',
      },
      {
        dimension: 'Music-org structure',
        fugueLab: 'Programs, faculty, repertoire, events, and applications modeled directly.',
        them: 'Generic pages and collections you adapt yourself.',
      },
      {
        dimension: 'Applications and auditions',
        fugueLab: 'Application funnels with payments and review workflow, on your own domain.',
        them: 'Third-party form embeds. Applicants leave your design, and the data lives in another tool.',
      },
      {
        dimension: 'Who does the work',
        fugueLab: 'We design, build, and maintain it. You edit content when you want to.',
        them: 'You, or a freelancer you find and manage.',
      },
      {
        dimension: 'Performance and SEO',
        fugueLab: 'Prerendered static pages. Every route ships real HTML that search engines index.',
        them: 'Depends on the template and what has been embedded into it.',
      },
      {
        dimension: 'Content editing',
        fugueLab: 'A website editor scoped to your content. You can change the words and images, not break the layout.',
        them: 'Full drag-and-drop editor, which is also how layouts get accidentally broken.',
      },
    ],
    whereTheyFit: {
      heading: 'Where Squarespace fits',
      body: 'A soloist who needs a bio, photos, and a contact form can be live on Squarespace this weekend for very little money, and that is a reasonable call. The math changes when there is an organization behind the site: seasons, rosters, applications, and a volunteer or admin who inherits the maintenance. That is the point where a template stops being cheap.',
    },
    switchSteps: [
      {
        title: 'We audit what you have',
        description:
          'Content, pages, traffic, and what your current site is silently costing you in speed, search ranking, and workarounds.',
      },
      {
        title: 'We design and build the real thing',
        description: 'A site designed around your programs and season, reviewed with your team before launch.',
      },
      {
        title: 'Launch without losing your history',
        description:
          'Your domain moves cleanly, old URLs redirect, and search engines keep everything you have earned.',
      },
    ],
    faqs: [
      {
        question: 'We already paid for a year of Squarespace. Does switching make sense mid-cycle?',
        answer:
          'Usually the decision is about the season, not the subscription. If applications or a season launch is coming, the remaining subscription cost is small next to what a site that converts is worth. If nothing is coming up, finish the year and plan the rebuild for the off-season.',
      },
      {
        question: 'Can we still edit our own content?',
        answer:
          'Yes. You get a website editor for the content that changes: events, rosters, news, programs. The design itself stays fixed, which is the point.',
      },
      {
        question: 'What does this cost compared to Squarespace?',
        answer:
          'More than a template subscription, because it includes the design, the build, and a person maintaining it. Packages are on the pricing page, and the call gives you an exact number for your situation.',
      },
    ],
    ctaHeading: 'See what your site could be without the template',
    ctaBody:
      'Book a call and bring your current site. You get a straight assessment of what is working, what is costing you, and what a rebuild designed for you would look like.',
  },
  {
    slug: 'wix',
    name: 'Wix',
    seoTitle: 'Wix Alternative for Music Organizations',
    seoDescription:
      'Compare Fugue Lab and Wix for music festivals, ensembles, schools, and churches. A fast, professionally maintained website designed for your organization instead of a drag-and-drop builder you manage yourself.',
    heroHeadline: 'Your festival deserves better than a website builder',
    heroBody:
      'Wix makes it possible for anyone to publish a website. It does not make the website good, fast, or right for a music organization with a season to sell and applicants to convert. Those are different problems.',
    shortVersion:
      'Wix is a drag-and-drop builder: you pick a template, arrange blocks, and bolt on apps from its marketplace as needs come up. It works until the site matters. Slow pages, a design that reads as template, and an app stack nobody remembers configuring are what music organizations usually bring us when they leave Wix. Fugue Lab replaces that with a fast, prerendered site designed around your programs, maintained by the person who built it.',
    rows: [
      {
        dimension: 'How it gets built',
        fugueLab: 'Designed and coded for your organization by a musician who understands the domain.',
        them: 'You assemble it from template blocks and marketplace apps.',
      },
      {
        dimension: 'Speed',
        fugueLab: 'Prerendered static pages served from a CDN. Fast on a phone on festival-lawn wifi.',
        them: 'Builder output carries the editor runtime with it. Heavier pages are the norm.',
      },
      {
        dimension: 'Template lock-in',
        fugueLab: 'No template. The design is yours and evolves with you.',
        them: 'Changing templates generally means rebuilding the site.',
      },
      {
        dimension: 'Features you need',
        fugueLab:
          'Application funnels, event calendars, email capture, and payments built in as part of the engagement.',
        them: 'Marketplace apps with separate settings, and often separate fees.',
      },
      {
        dimension: 'Maintenance',
        fugueLab: 'Ours. Content edits are yours through the website editor.',
        them: 'Yours.',
      },
      {
        dimension: 'SEO',
        fugueLab: 'Real HTML per route, structured data, and redirects handled at launch.',
        them: 'Workable, but dependent on how the site was assembled.',
      },
    ],
    whereTheyFit: {
      heading: 'Where Wix fits',
      body: 'For a personal page or a brand-new group that needs any web presence this week at minimal cost, Wix does that job. The organizations that come to us from Wix are past that stage: they have programs to fill and an audience to grow, and the site has become the bottleneck instead of the tool.',
    },
    switchSteps: [
      {
        title: 'We inventory the Wix site',
        description:
          'Pages, embedded apps, forms, and where your data actually lives, so nothing gets lost in the move.',
      },
      {
        title: 'We build the replacement',
        description:
          'A new design, your content restructured around programs and season, application and contact flows rebuilt natively.',
      },
      {
        title: 'Domain and search move with you',
        description: 'DNS cutover, redirects for every old URL, and verification that rankings carry over.',
      },
    ],
    faqs: [
      {
        question: 'We have years of content on Wix. Does it all move?',
        answer:
          'Yes. Content migration is part of the build. Pages, images, blog posts, and event history come across, usually better organized than they were.',
      },
      {
        question: 'Our box office and forms run through Wix apps. What happens to those?',
        answer:
          'Each one gets inventoried and either rebuilt natively (forms, applications, email capture) or reconnected (external box office links). You see the plan before anything moves.',
      },
      {
        question: 'Is this overkill for a small ensemble?',
        answer:
          'Sometimes, honestly. If a builder page is serving you fine and nothing is riding on the site, keep it. When concerts, applications, or donations depend on the site working well, that is when the upgrade pays for itself.',
      },
    ],
    ctaHeading: 'Replace the builder with a site that carries its weight',
    ctaBody:
      'Book a call, share your Wix site, and get a concrete migration plan with timeline and cost. No rebuild starts until you have seen exactly what changes.',
  },
  {
    slug: 'wordpress',
    name: 'WordPress',
    seoTitle: 'WordPress Alternative for Music Organizations',
    seoDescription:
      'Compare Fugue Lab and WordPress for music festivals, ensembles, schools, and churches. A prerendered site with nothing to patch instead of a plugin stack that needs constant maintenance.',
    heroHeadline: 'Everything WordPress does for a music organization, without the maintenance treadmill',
    heroBody:
      'WordPress can do anything, which is exactly the problem. Somebody has to choose the theme, configure the page builder, vet the plugins, apply the security updates, and fix it when an update breaks the season page the week before opening night.',
    shortVersion:
      'WordPress powers a huge share of the web, and it earns that with flexibility. For a music organization, that flexibility usually arrives as a theme, a page builder, a dozen plugins, hosting, and a standing maintenance bill to keep the whole stack patched and compatible. Fugue Lab takes a different approach: your site is prerendered to static pages, so there is no plugin stack to patch and nothing for an attacker to log into. You edit content through a website editor; we handle everything else.',
    rows: [
      {
        dimension: 'Architecture',
        fugueLab: 'Prerendered static pages on a CDN. No server, no admin login surface, nothing to patch.',
        them: 'PHP application plus theme plus plugins, all needing updates and compatibility care.',
      },
      {
        dimension: 'Security',
        fugueLab: 'Static output. The common WordPress attack surface simply is not there.',
        them: 'A perpetual patching discipline. Outdated plugins are the classic way music-org sites get hacked.',
      },
      {
        dimension: 'Maintenance cost',
        fugueLab: 'Included in the engagement. One relationship, one bill.',
        them: 'Hosting, plugin licenses, and typically a developer or agency retainer to keep it healthy.',
      },
      {
        dimension: 'Editing experience',
        fugueLab: 'A website editor scoped to your content types: events, programs, people, news.',
        them: 'wp-admin, with the page builder your last developer happened to choose.',
      },
      {
        dimension: 'Speed',
        fugueLab: 'Static HTML, optimized images, no runtime database calls.',
        them: 'Ranges from decent to painful depending on theme, plugins, and hosting.',
      },
      {
        dimension: 'Applications and payments',
        fugueLab:
          'Native application funnels with integrated payments (Stripe, PayPal, or your preferred system) and review workflow.',
        them: 'Form plugins plus payment add-ons, assembled and maintained by you.',
      },
    ],
    whereTheyFit: {
      heading: 'Where WordPress fits',
      body: 'With a skilled developer permanently attached, WordPress remains a capable platform, and organizations that have that person on staff are usually well served. The pattern we see instead: the developer who set it up moved on years ago, updates are behind, and nobody currently at the organization knows which plugin does what. If that sounds familiar, the platform is not the problem you need to solve. The dependency is.',
    },
    switchSteps: [
      {
        title: 'We take a full export',
        description:
          'Content, media, and URL inventory out of WordPress before anything is touched. Your history is safe from step one.',
      },
      {
        title: 'We rebuild without the stack',
        description:
          'A new design, content restructured for your programs, forms and payments rebuilt natively. No theme, no plugins.',
      },
      {
        title: 'Cut over and decommission',
        description:
          'Redirects preserve every ranking URL, the old hosting gets retired, and the maintenance bill retires with it.',
      },
    ],
    faqs: [
      {
        question: 'We have hundreds of posts and pages. Can they all come over?',
        answer:
          'Yes. WordPress exports cleanly, and migrating the archive, with redirects for every URL, is a standard part of the build.',
      },
      {
        question: 'Our current site was hacked or is behind on updates. Where do we start?',
        answer:
          'Start with the call. We take the content export early so nothing more can be lost, and if the site is actively compromised we prioritize getting a clean replacement live.',
      },
      {
        question: 'What do we stop paying for after the switch?',
        answer:
          'Typically hosting, premium plugin renewals, and the maintenance retainer. What replaces them is one flat engagement with us, listed on the pricing page.',
      },
    ],
    ctaHeading: 'Retire the maintenance treadmill',
    ctaBody:
      'Book a call and bring your WordPress site, whatever state it is in. You get an honest read on what a rebuild involves and what stops being your problem afterward.',
  },
  {
    slug: 'eventbrite',
    name: 'Eventbrite',
    seoTitle: 'Eventbrite Alternative for Music Organizations',
    seoDescription:
      'Compare Fugue Lab and Eventbrite for concerts and music events. Sell tickets on your own website with 1.5% per ticket instead of stacked service fees, and keep your audience data.',
    heroHeadline: 'Sell tickets on your own site, not on a marketplace',
    heroBody:
      'Eventbrite puts your concert on their platform, wraps it in their fees, and keeps the relationship with your buyers. For a music organization that sells the same audience season after season, that trade gets expensive.',
    shortVersion:
      'Eventbrite is a general-purpose events marketplace. Listing is easy, and its published US fees stack up per paid ticket: a 3.7% service fee, plus $1.79 per ticket, plus 2.9% payment processing, usually surfaced to your patrons at checkout. Fugue Lab ticketing runs on your own website for a flat monthly rate plus 1.5% per ticket (card processing via your own Stripe account), with QR check-in, reporting, and every buyer landing in your audience record next to your donors and subscribers.',
    rows: [
      {
        dimension: 'Fees per paid ticket',
        fugueLab: '1.5% per ticket, plus standard Stripe card processing paid to Stripe.',
        them: 'Published US fees: 3.7% service fee + $1.79 per ticket + 2.9% payment processing.',
      },
      {
        dimension: 'Where tickets sell',
        fugueLab: 'On your own website, in your own design, on your own domain.',
        them: 'On an Eventbrite event page, alongside other organizations’ events.',
      },
      {
        dimension: 'Your audience data',
        fugueLab: 'Every buyer is one record with their email and giving history. Yours, exportable, free.',
        them: 'Buyer relationships live in Eventbrite; attendees are also Eventbrite users it can market to.',
      },
      {
        dimension: 'The money',
        fugueLab: 'Straight to your own Stripe account at sale time.',
        them: 'Collected by Eventbrite and paid out on its schedule after the event.',
      },
      {
        dimension: 'Free events and RSVPs',
        fugueLab: 'No per-ticket fee. RSVP and check-in included.',
        them: 'Free tickets are free on Eventbrite as well.',
      },
      {
        dimension: 'Event night',
        fugueLab: 'QR check-in and reporting, with Concierge covering full box-office staffing if you want it.',
        them: 'Organizer app for check-in; support is a platform queue.',
      },
    ],
    whereTheyFit: {
      heading: 'Where Eventbrite fits',
      body: 'Eventbrite earns its fees when discovery matters: a public event that strangers should stumble into while browsing a city’s listings. Some organizations keep a listing there for that reason alone. A season concert series is different: your buyers are your own audience, they come from your email list and your website, and paying marketplace fees on people you already reached is the part that stops making sense.',
    },
    switchSteps: [
      {
        title: 'We map your season',
        description: 'Events, tiers, capacities, comps, and how your current Eventbrite checkout is configured.',
      },
      {
        title: 'Ticketing goes live on your site',
        description:
          'Your events sell on your own domain with your branding, connected to your own Stripe account, tested before on-sale.',
      },
      {
        title: 'Your audience comes home',
        description:
          'Past attendee exports import into your audience record, so next season’s announcement reaches everyone who came before.',
      },
    ],
    faqs: [
      {
        question: 'What do the fees actually look like on a real ticket?',
        answer:
          'On a $25 ticket: with us, 1.5% is 38 cents, plus standard Stripe processing paid to Stripe. On Eventbrite’s published US fees, the service fee and per-ticket charge alone are about $2.72 before its 2.9% processing. Multiply by a season of tickets and the difference funds real programming.',
      },
      {
        question: 'Can patrons still pay the fees instead of us?',
        answer:
          'Yes. Like most ticketing, you choose whether fees are absorbed in the price or added at checkout. Ours are small enough that many organizations simply absorb them.',
      },
      {
        question: 'We rely on Eventbrite for discovery. Do we lose that?',
        answer:
          'Nothing stops you keeping a listing there for discovery while your real box office runs on your own site. Organizations that sell mostly to their own audience usually find the listing was doing less than the fees implied.',
      },
      {
        question: 'Who runs it?',
        answer:
          'Today our team sets up and runs ticketing for you as part of the engagement, with self-serve controls rolling out. Full box-office operations, on-sale management and event-day check-in included, live in the Concierge tier.',
      },
    ],
    ctaHeading: 'Bring the box office home',
    ctaBody:
      'Book a call and bring last season’s ticket volume. You get the fee math for your actual numbers and a concrete picture of ticketing on your own site.',
  },
]

export function getCompetitor(slug: string): Competitor {
  const competitor = competitors.find((c) => c.slug === slug)
  if (!competitor) throw new Error(`Unknown competitor slug: ${slug}`)
  return competitor
}
